import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import Konva from 'konva';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Circle, Group, Rect, Transformer } from 'react-konva';
import { maskEditorAtom } from '@/store/boardAtoms';
/**
 * Трансформер для редактирования маски элемента
 */
const MaskTransformer = ({ boardState, setBoardState, send }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const transformerRef = useRef(null);
    const rectRef = useRef(null);
    const circleRef = useRef(null);
    const anchorRef = useRef(null);
    const groupRef = useRef(null);
    const updatesRef = useRef([]);
    const [maskEditor, setMaskEditor] = useAtom(maskEditorAtom);
    const sendUpdatedMask = useCallback(() => {
        if (!maskEditor.active || !maskEditor.element)
            return;
        send({
            type: 'element.update',
            payload: updatesRef.current,
        });
        setMaskEditor({ active: false, element: null, mode: 'rect', rect: null, circle: null });
    }, [maskEditor.active, maskEditor.element, send, setMaskEditor]);
    // Применяем трансформер к маске
    useEffect(() => {
        var _a, _b, _c;
        if (!maskEditor.active || !maskEditor.element)
            return;
        const transformer = transformerRef.current;
        const existingClip = (_a = maskEditor.element.props) === null || _a === void 0 ? void 0 : _a.clip;
        if (!transformer)
            return;
        // Определяем тип маски и применяем трансформер к соответствующему элементу
        if ((existingClip === null || existingClip === void 0 ? void 0 : existingClip.type) === 'circle') {
            const circle = circleRef.current;
            if (circle) {
                transformer.nodes([circle]);
                (_b = transformer.getLayer()) === null || _b === void 0 ? void 0 : _b.batchDraw();
            }
        }
        else {
            const rect = rectRef.current;
            if (rect) {
                transformer.nodes([rect]);
                (_c = transformer.getLayer()) === null || _c === void 0 ? void 0 : _c.batchDraw();
            }
        }
    }, [maskEditor.active, maskEditor.element]);
    // Обновляем позицию и размеры маски при изменении данных элемента
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!maskEditor.active || !maskEditor.element)
            return;
        const existingClip = (_a = maskEditor.element.props) === null || _a === void 0 ? void 0 : _a.clip;
        const elementW = ((_b = maskEditor.element.props) === null || _b === void 0 ? void 0 : _b.w) || 64;
        const elementH = ((_c = maskEditor.element.props) === null || _c === void 0 ? void 0 : _c.h) || 64;
        if ((existingClip === null || existingClip === void 0 ? void 0 : existingClip.type) === 'circle') {
            const circle = circleRef.current;
            if (circle) {
                const radius = (_d = existingClip.r) !== null && _d !== void 0 ? _d : Math.min(elementW, elementH) / 2;
                circle.x(elementW / 2);
                circle.y(elementH / 2);
                circle.radius(radius);
                (_e = circle.getLayer()) === null || _e === void 0 ? void 0 : _e.batchDraw();
            }
        }
        else {
            const rect = rectRef.current;
            if (rect) {
                if (existingClip && existingClip.type === 'rect') {
                    rect.x(existingClip.x || 0);
                    rect.y(existingClip.y || 0);
                    rect.width(existingClip.w || elementW);
                    rect.height(existingClip.h || elementH);
                }
                else {
                    rect.x(0);
                    rect.y(0);
                    rect.width(elementW);
                    rect.height(elementH);
                }
                (_f = rect.getLayer()) === null || _f === void 0 ? void 0 : _f.batchDraw();
            }
        }
    }, [
        maskEditor.active,
        maskEditor.element,
        (_b = (_a = maskEditor.element) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.clip,
        (_d = (_c = maskEditor.element) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.w,
        (_f = (_e = maskEditor.element) === null || _e === void 0 ? void 0 : _e.props) === null || _f === void 0 ? void 0 : _f.h,
    ]);
    // Обработчик клика вне трансформера маски
    const handleStageClick = useCallback((event) => {
        var _a;
        if (!maskEditor.active)
            return;
        const stage = (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.getStage();
        if (!stage)
            return;
        // Получаем цель клика
        let target = event.target;
        // Проверяем, является ли клик по элементам трансформера маски
        let isMaskTransformerClick = false;
        while (target) {
            if (target.name() === 'mask-rect' ||
                target.name() === 'mask-circle' ||
                target.name() === 'mask-transformer') {
                isMaskTransformerClick = true;
                break;
            }
            if (!target.parent)
                break;
            target = target.parent;
        }
        // Если клик не по трансформеру маски, закрываем редактор
        if (!isMaskTransformerClick) {
            sendUpdatedMask();
        }
    }, [maskEditor.active, sendUpdatedMask]);
    // Добавляем обработчик клика на Stage при активации редактора маски
    useEffect(() => {
        var _a;
        if (!maskEditor.active)
            return;
        const stage = (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.getStage();
        if (!stage)
            return;
        // Добавляем обработчик с небольшой задержкой, чтобы избежать срабатывания на том же клике, который активировал редактор
        const timeoutId = setTimeout(() => {
            stage.on('mousedown', handleStageClick);
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            stage.off('mousedown', handleStageClick);
        };
    }, [maskEditor.active, handleStageClick]);
    // Ограничиваем позицию и размеры маски в пределах элемента
    const constrainMask = useCallback((mask) => {
        var _a, _b, _c, _d;
        const elementW = ((_b = (_a = maskEditor.element) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.w) || 64;
        const elementH = ((_d = (_c = maskEditor.element) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
        if (mask instanceof Konva.Rect) {
            // Ограничиваем позицию
            let x = Math.max(0, Math.min(mask.x(), elementW - mask.width()));
            let y = Math.max(0, Math.min(mask.y(), elementH - mask.height()));
            // Ограничиваем размеры
            const width = Math.max(5, Math.min(mask.width(), elementW - x));
            const height = Math.max(5, Math.min(mask.height(), elementH - y));
            // Корректируем позицию если размеры изменились
            if (width !== mask.width()) {
                x = Math.max(0, Math.min(x, elementW - width));
            }
            if (height !== mask.height()) {
                y = Math.max(0, Math.min(y, elementH - height));
            }
            mask.x(x);
            mask.y(y);
            mask.width(width);
            mask.height(height);
        }
        else if (mask instanceof Konva.Circle) {
            const radius = Math.min(mask.radius(), Math.min(elementW, elementH) / 2);
            const x = Math.max(radius, Math.min(mask.x(), elementW - radius));
            const y = Math.max(radius, Math.min(mask.y(), elementH - radius));
            mask.x(x);
            mask.y(y);
            mask.radius(radius);
        }
    }, [maskEditor.element]);
    // Обработчик перетаскивания маски
    const handleMaskDragMove = useCallback(() => {
        if (!maskEditor.active || !maskEditor.element)
            return;
        const rect = rectRef.current;
        const circle = circleRef.current;
        const mask = rect || circle;
        if (!mask)
            return;
        constrainMask(mask);
    }, [maskEditor.active, maskEditor.element, constrainMask]);
    // Обработчик начала трансформации маски
    const handleMaskTransformStart = useCallback(() => {
        if (!maskEditor.active || !maskEditor.element)
            return;
        const rect = rectRef.current;
        const circle = circleRef.current;
        const mask = rect || circle;
        if (!mask)
            return;
        // Сохраняем начальные значения для ограничений
        mask.setAttr('_originalX', mask.x());
        mask.setAttr('_originalY', mask.y());
        mask.setAttr('_originalWidth', mask.width());
        mask.setAttr('_originalHeight', mask.height());
    }, [maskEditor.active, maskEditor.element]);
    // Обработчик трансформации маски в реальном времени
    const handleMaskTransformMove = useCallback(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!maskEditor.active || !maskEditor.element)
            return;
        const rect = rectRef.current;
        const circle = circleRef.current;
        const mask = rect || circle;
        if (!mask)
            return;
        const elementW = ((_b = (_a = maskEditor.element) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.w) || 64;
        const elementH = ((_d = (_c = maskEditor.element) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
        if (mask instanceof Konva.Rect) {
            // Получаем текущие значения с учетом масштаба
            const scaleX = mask.scaleX();
            const scaleY = mask.scaleY();
            const width = mask.width() * scaleX;
            const height = mask.height() * scaleY;
            const x = mask.x();
            const y = mask.y();
            // Ограничиваем размеры
            const constrainedWidth = Math.max(5, Math.min(width, elementW));
            const constrainedHeight = Math.max(5, Math.min(height, elementH));
            // Ограничиваем позицию
            const constrainedX = Math.max(0, Math.min(x, elementW - constrainedWidth));
            const constrainedY = Math.max(0, Math.min(y, elementH - constrainedHeight));
            // Применяем ограничения только если они отличаются
            if (constrainedX !== x ||
                constrainedY !== y ||
                constrainedWidth !== width ||
                constrainedHeight !== height) {
                mask.x(constrainedX);
                mask.y(constrainedY);
                mask.scaleX(constrainedWidth / mask.width());
                mask.scaleY(constrainedHeight / mask.height());
                (_e = mask.getLayer()) === null || _e === void 0 ? void 0 : _e.batchDraw();
            }
        }
        else if (mask instanceof Konva.Circle) {
            const scaleX = mask.scaleX();
            const radius = mask.radius() * scaleX;
            const x = mask.x();
            const y = mask.y();
            // Ограничиваем радиус
            const constrainedRadius = Math.min(radius, Math.min(elementW, elementH) / 2);
            // Ограничиваем позицию
            const constrainedX = Math.max(constrainedRadius, Math.min(x, elementW - constrainedRadius));
            const constrainedY = Math.max(constrainedRadius, Math.min(y, elementH - constrainedRadius));
            // Применяем ограничения только если они отличаются
            if (constrainedX !== x || constrainedY !== y || constrainedRadius !== radius) {
                mask.x(constrainedX);
                mask.y(constrainedY);
                mask.scaleX(constrainedRadius / mask.radius());
                mask.scaleY(constrainedRadius / mask.radius());
                (_f = mask.getLayer()) === null || _f === void 0 ? void 0 : _f.batchDraw();
            }
        }
    }, [maskEditor.active, maskEditor.element]);
    // Обработчик завершения трансформации маски
    const handleMaskTransformEnd = useCallback(() => {
        if (!maskEditor.active || !maskEditor.element)
            return;
        const rect = rectRef.current;
        const circle = circleRef.current;
        const anchor = anchorRef.current;
        if ((!rect && !circle) || !anchor)
            return;
        const mask = rect || circle;
        if (!mask)
            return;
        const maskType = rect ? 'rect' : 'circle';
        // Получаем координаты маски относительно якоря (начала элемента)
        const anchorX = anchor.x();
        const anchorY = anchor.y();
        const maskX = mask.x();
        const maskY = mask.y();
        // Рассчитываем координаты маски относительно элемента (0,0)
        const relativeX = maskX - anchorX;
        const relativeY = maskY - anchorY;
        const newRect = {
            x: relativeX,
            y: relativeY,
            w: mask.width() * mask.scaleX(),
            h: mask.height() * mask.scaleY(),
        };
        // Сбрасываем масштаб
        mask.scaleX(1);
        mask.scaleY(1);
        mask.width(newRect.w);
        mask.height(newRect.h);
        // Обновляем элемент
        const updates = [
            {
                id: maskEditor.element.id,
                props: { clip: { type: maskType, x: newRect.x, y: newRect.y, w: newRect.w, h: newRect.h } },
            },
        ];
        setBoardState((prev) => {
            var _a;
            const next = Object.assign({}, prev);
            for (const layer of next.layers || []) {
                for (const el of layer.elements || []) {
                    if (el.id === ((_a = maskEditor.element) === null || _a === void 0 ? void 0 : _a.id)) {
                        el.props = Object.assign(Object.assign({}, (el.props || {})), { clip: { type: maskType, x: newRect.x, y: newRect.y, w: newRect.w, h: newRect.h } });
                    }
                }
            }
            return next;
        });
        updatesRef.current = updates;
    }, [maskEditor.active, maskEditor.element, setBoardState]);
    const handleBoundBoxFunc = useCallback((oldBox, newBox) => {
        // Предотвращаем флип - размеры должны быть положительными
        if (newBox.width < 0) {
            newBox.width = Math.abs(newBox.width);
            newBox.x = oldBox.x + oldBox.width - newBox.width;
        }
        if (newBox.height < 0) {
            newBox.height = Math.abs(newBox.height);
            newBox.y = oldBox.y + oldBox.height - newBox.height;
        }
        // Минимальные размеры
        if (newBox.width < 50 || newBox.height < 50) {
            return oldBox;
        }
        // // Максимальные размеры
        // if (newBox.width > 200 || newBox.height > 200) {
        //   return oldBox;
        // }
        return newBox;
    }, []);
    if (!maskEditor.active || !((_g = maskEditor.element) === null || _g === void 0 ? void 0 : _g.id)) {
        return null;
    }
    // Находим элемент для редактирования маски - мемоизируем результат поиска
    const targetElement = useMemo(() => {
        var _a;
        for (const layer of boardState.layers || []) {
            for (const element of layer.elements || []) {
                if (element.id === ((_a = maskEditor.element) === null || _a === void 0 ? void 0 : _a.id)) {
                    return element;
                }
            }
        }
        return null;
    }, [boardState.layers, (_h = maskEditor.element) === null || _h === void 0 ? void 0 : _h.id]);
    if (!targetElement)
        return null;
    const elementX = ((_j = targetElement.props) === null || _j === void 0 ? void 0 : _j.x) || 0;
    const elementY = ((_k = targetElement.props) === null || _k === void 0 ? void 0 : _k.y) || 0;
    const elementW = ((_l = targetElement.props) === null || _l === void 0 ? void 0 : _l.w) || 64;
    const elementH = ((_m = targetElement.props) === null || _m === void 0 ? void 0 : _m.h) || 64;
    // Инициализируем маску из актуальных данных элемента
    const existingClip = (_o = targetElement.props) === null || _o === void 0 ? void 0 : _o.clip;
    const initialRect = existingClip && existingClip.type === 'rect'
        ? {
            x: existingClip.x || 0,
            y: existingClip.y || 0,
            w: existingClip.w || elementW,
            h: existingClip.h || elementH,
        }
        : { x: 0, y: 0, w: elementW, h: elementH };
    const initialCircle = existingClip && existingClip.type === 'circle'
        ? {
            x: elementW / 2,
            y: elementH / 2,
            radius: (_p = existingClip.r) !== null && _p !== void 0 ? _p : Math.min(elementW, elementH) / 2,
        }
        : { x: elementW / 2, y: elementH / 2, radius: Math.min(elementW, elementH) / 2 };
    return (_jsxs(Group, { ref: groupRef, x: elementX, y: elementY, children: [_jsx(Rect, { ref: anchorRef, x: 0, y: 0, width: 0, height: 0, visible: false, listening: false }), (existingClip === null || existingClip === void 0 ? void 0 : existingClip.type) === 'circle' ? (_jsx(Circle, { ref: circleRef, x: initialCircle.x, y: initialCircle.y, radius: initialCircle.radius, stroke: "#33aaff", strokeWidth: 2, strokeScaleEnabled: false, fill: "rgba(51, 170, 255, 0.1)", draggable: true, name: "mask-circle" })) : (_jsx(Rect, { ref: rectRef, x: initialRect.x, y: initialRect.y, width: initialRect.w, height: initialRect.h, stroke: "#33aaff", strokeWidth: 2, strokeScaleEnabled: false, fill: "rgba(51, 170, 255, 0.1)", draggable: true, name: "mask-rect" })), _jsx(Transformer, { ref: transformerRef, flipEnabled: false, rotateEnabled: false, borderEnabled: false, name: "mask-transformer", keepRatio: false, ignoreStroke: true, boundBoxFunc: handleBoundBoxFunc, onDragMove: handleMaskDragMove, onDragEnd: handleMaskTransformEnd, onTransformStart: handleMaskTransformStart, onTransformMove: handleMaskTransformMove, onTransformEnd: handleMaskTransformEnd })] }));
};
export default memo(MaskTransformer, (prevProps, nextProps) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    // Перерендериваем только если изменился активный элемент маски или его свойства
    // maskEditor берется из атома внутри компонента, поэтому сравниваем boardState
    const prevActiveElementId = (_c = (_b = (_a = prevProps.boardState) === null || _a === void 0 ? void 0 : _a.layers) === null || _b === void 0 ? void 0 : _b.flatMap((l) => l.elements || []).find((el) => { var _a; 
    // Ищем элемент, который может быть выбран для редактирования маски
    return ((_a = el.props) === null || _a === void 0 ? void 0 : _a.clip) !== undefined; })) === null || _c === void 0 ? void 0 : _c.id;
    const nextActiveElementId = (_f = (_e = (_d = nextProps.boardState) === null || _d === void 0 ? void 0 : _d.layers) === null || _e === void 0 ? void 0 : _e.flatMap((l) => l.elements || []).find((el) => { var _a; return ((_a = el.props) === null || _a === void 0 ? void 0 : _a.clip) !== undefined; })) === null || _f === void 0 ? void 0 : _f.id;
    // Сравниваем boardState.layers - если изменились элементы, которые могут иметь маску
    const prevLayersHash = JSON.stringify((_h = (_g = prevProps.boardState) === null || _g === void 0 ? void 0 : _g.layers) === null || _h === void 0 ? void 0 : _h.map((l) => {
        var _a;
        return ({
            id: l.id,
            elements: (_a = l.elements) === null || _a === void 0 ? void 0 : _a.map((el) => {
                var _a, _b, _c, _d, _e;
                return ({
                    id: el.id,
                    clip: (_a = el.props) === null || _a === void 0 ? void 0 : _a.clip,
                    x: (_b = el.props) === null || _b === void 0 ? void 0 : _b.x,
                    y: (_c = el.props) === null || _c === void 0 ? void 0 : _c.y,
                    w: (_d = el.props) === null || _d === void 0 ? void 0 : _d.w,
                    h: (_e = el.props) === null || _e === void 0 ? void 0 : _e.h,
                });
            }),
        });
    }));
    const nextLayersHash = JSON.stringify((_k = (_j = nextProps.boardState) === null || _j === void 0 ? void 0 : _j.layers) === null || _k === void 0 ? void 0 : _k.map((l) => {
        var _a;
        return ({
            id: l.id,
            elements: (_a = l.elements) === null || _a === void 0 ? void 0 : _a.map((el) => {
                var _a, _b, _c, _d, _e;
                return ({
                    id: el.id,
                    clip: (_a = el.props) === null || _a === void 0 ? void 0 : _a.clip,
                    x: (_b = el.props) === null || _b === void 0 ? void 0 : _b.x,
                    y: (_c = el.props) === null || _c === void 0 ? void 0 : _c.y,
                    w: (_d = el.props) === null || _d === void 0 ? void 0 : _d.w,
                    h: (_e = el.props) === null || _e === void 0 ? void 0 : _e.h,
                });
            }),
        });
    }));
    return (prevLayersHash === nextLayersHash &&
        prevProps.setBoardState === nextProps.setBoardState &&
        prevProps.send === nextProps.send);
});
